/**
 * TochGeslaagdDialog — bevestigingsdialoog voor de office-actie "stop toch
 * geslaagd": een eerder als "niet gelukt" gemarkeerde (overgeslagen) ophaal of
 * bezorging alsnog als geslaagd markeren.
 *
 * Spiegel van NietGeluktDialog. Use-case: chauffeur deed "Niet thuis" en reed
 * door; klant belde terug, kleden zijn alsnog opgehaald. Bij bevestigen gaat de
 * werkorder naar 'Opgehaald'/'Teruggebracht', de kleden schuiven door, het
 * no-show transport-item wordt verwijderd en de klant krijgt de normale
 * ophaal-/bezorgbevestiging (zoals bij elke geslaagde rit).
 *
 * Tweede gebruik (23-10): een stop die de chauffeur nooit afmeldde terwijl de
 * route al is afgerond. Zelfde dialoog, zelfde verb; de server herkent het
 * geval aan de routestatus.
 */

import { useState, useCallback, useEffect } from 'react';
import type { RouteStop } from '../../lib/salesforce/types';

interface TochGeslaagdDialogProps {
  stops: RouteStop[];
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (opts: { notitie?: string }) => void;
  isSubmitting: boolean;
}

export function TochGeslaagdDialog({
  stops,
  isOpen,
  onClose,
  onConfirm,
  isSubmitting,
}: TochGeslaagdDialogProps) {
  const [notitie, setNotitie] = useState('');

  // Reset het veld telkens als de dialog opent met een nieuwe selectie
  useEffect(() => {
    if (isOpen) {
      setNotitie('');
    }
  }, [isOpen]);

  const handleConfirm = useCallback(() => {
    onConfirm({ notitie: notitie.trim() || undefined });
  }, [notitie, onConfirm]);

  if (!isOpen) return null;

  const count = stops.length;
  const meervoud = count !== 1;
  const alleenOphalen = stops.every((s) => s.stopType === 'Ophalen');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Dialog */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {meervoud ? `${count} stops alsnog geslaagd` : 'Stop alsnog geslaagd'}
        </h3>

        <p className="text-sm text-gray-600 mb-4">
          {alleenOphalen
            ? 'De werkorder(s) gaan naar Opgehaald, de kleden schuiven door in de wasserij en een eventuele no-show-regel wordt teruggedraaid. De klant krijgt de normale ophaalbevestiging.'
            : 'De werkorder(s) worden afgerond (Opgehaald/Teruggebracht), de kleden schuiven door en een eventuele no-show-regel wordt teruggedraaid. De klant krijgt de normale bevestiging.'}
        </p>

        {/* Betrokken klant(en) */}
        <div className="mb-4 max-h-40 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
          {stops.map((stop) => (
            <div key={stop.id} className="flex items-center justify-between gap-2 px-3 py-2">
              <div className="min-w-0">
                <div className="font-medium text-gray-900 truncate">
                  {stop.accountName || stop.contactName || 'Onbekende klant'}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {stop.street}, {stop.postalCode} {stop.city}
                </div>
              </div>
              <span
                className={`shrink-0 px-1.5 py-0.5 rounded text-xs font-medium ${
                  stop.stopType === 'Ophalen'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-emerald-100 text-emerald-700'
                }`}
              >
                {stop.stopType}
              </span>
            </div>
          ))}
        </div>

        {/* Notitie */}
        <div className="mb-4">
          <label htmlFor="toch-geslaagd-notitie" className="block text-sm font-medium text-gray-700 mb-1">
            Notitie <span className="font-normal text-gray-400">(optioneel)</span>
          </label>
          <textarea
            id="toch-geslaagd-notitie"
            value={notitie}
            onChange={(e) => setNotitie(e.target.value)}
            rows={2}
            placeholder="Bijv. klant belde terug, chauffeur is teruggegaan"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
          />
          <p className="mt-1 text-xs text-gray-400">
            Komt in de planningsnotitie van de werkorder.
          </p>
        </div>

        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting || count === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Bezig…
              </>
            ) : (
              'Markeer als geslaagd'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
